import { DEFAULT_PAGE_SIZE } from '@w7/ui'

/**
 * URL-persisted ticker filters.
 *
 * Defaults are never written to the URL. Unknown values drop to the default.
 * Any filter change other than page/size resets the page to 1 — paging into a
 * result set that no longer exists is the classic empty-page bug.
 *
 * Every filter here maps to a parameter `/v1/book/ticker` binds. Nothing is
 * accepted that the API cannot honour.
 */

import {
  CURRENT_TICKER_STATUSES,
  DEFAULT_TICKER_FILTERS,
  PREVIOUS_RANGE_MAX_DAYS,
  TICKER_BQ_SPAN_DAYS,
  PREVIOUS_TICKER_STATUSES,
  TICKER_MARKET_TYPES,
  TICKER_PAGE_SIZES,
  type BetSide,
  type TickerFilters,
  type TickerMarketType,
  type TickerPageSize,
} from '../domain'

const SEARCH_MAX = 80
const MEMBER_MAX = 40
const MEMBER_LEN = 64
const STAKE_MAX = 10_000_000

const SIDES = new Set<BetSide | 'all'>(['all', 'BACK', 'LAY'])
const PAGE_SIZES = new Set<number>(TICKER_PAGE_SIZES)
const ALL_STATUS = new Set<string>([...CURRENT_TICKER_STATUSES, ...PREVIOUS_TICKER_STATUSES])

function sanitizeMarketType(raw: string): TickerMarketType | 'all' {
  const value = stripControls(raw).trim()
  if (!value || value.toLowerCase() === 'all') return 'all'
  const hit = TICKER_MARKET_TYPES.find((t) => t.toLowerCase() === value.toLowerCase())
  return hit ?? 'all'
}

function sanitizeStatus(raw: string): string {
  const value = stripControls(raw).trim().toUpperCase()
  if (!value || value === 'ALL') return 'all'
  return ALL_STATUS.has(value) ? value : 'all'
}

function stripControls(raw: string): string {
  let out = ''
  for (const ch of raw) {
    const code = ch.charCodeAt(0)
    if (code < 32 || code === 127) continue
    out += ch
  }
  return out
}

export function sanitizeSearch(raw: string): string {
  return stripControls(raw).trim().slice(0, SEARCH_MAX)
}

function sanitizeToken(raw: string, max = 80): string {
  return stripControls(raw).trim().slice(0, max)
}

function sanitizeMember(raw: string): string | null {
  const value = sanitizeToken(raw, MEMBER_LEN)
  if (!value) return null
  if (!/^[A-Za-z0-9._:@-]+$/.test(value)) return null
  return value
}

/** ISO-4217 shape. Anything else is a typo or a probe, not a currency. */
function sanitizeCurrency(raw: string): string {
  const value = sanitizeToken(raw, 8).toUpperCase()
  if (!value || value === 'ALL') return 'all'
  return /^[A-Z]{3}$/.test(value) ? value : 'all'
}

function parseStake(raw: string | null): number | null {
  if (raw == null || raw === '') return null
  const n = Number(raw)
  if (!Number.isFinite(n) || n < 0) return null
  return Math.min(STAKE_MAX, Math.round(n * 100) / 100)
}

function isIsoDay(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`))
}

/**
 * Keep the range inside what the API accepts, rather than letting the operator
 * submit a window the server will reject.
 */
function clampDateRange(from: string, to: string): { from: string; to: string } {
  let start = from && isIsoDay(from) ? from : ''
  let end = to && isIsoDay(to) ? to : ''
  if (start && end && start > end) {
    const swap = start
    start = end
    end = swap
  }
  if (start && end) {
    const days = (Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000
    if (days > PREVIOUS_RANGE_MAX_DAYS) {
      const capped = new Date(
        Date.parse(`${start}T00:00:00Z`) + PREVIOUS_RANGE_MAX_DAYS * 86_400_000,
      )
      end = capped.toISOString().slice(0, 10)
    }
  }
  return { from: start, to: end }
}

export function parseTickerFilters(params: URLSearchParams): TickerFilters {
  const members = params
    .getAll('member')
    .map(sanitizeMember)
    .filter((id): id is string => Boolean(id))
    .slice(0, MEMBER_MAX)

  const sideRaw = (params.get('side') ?? 'all').toUpperCase()
  const sizeRaw = Number(params.get('size') ?? DEFAULT_TICKER_FILTERS.pageSize)
  const pageRaw = Number(params.get('page') ?? 1)
  const { from, to } = clampDateRange(params.get('from') ?? '', params.get('to') ?? '')

  let stakeMin = parseStake(params.get('smin'))
  let stakeMax = parseStake(params.get('smax'))
  if (stakeMin != null && stakeMax != null && stakeMin > stakeMax) {
    const swap = stakeMin
    stakeMin = stakeMax
    stakeMax = swap
  }

  const pageSize = (PAGE_SIZES.has(sizeRaw) ? sizeRaw : DEFAULT_PAGE_SIZE) as TickerPageSize
  const page = Number.isInteger(pageRaw) && pageRaw >= 1 ? Math.min(pageRaw, 10_000) : 1

  return {
    members,
    search: sanitizeSearch(params.get('q') ?? ''),
    sport: sanitizeToken(params.get('sport') ?? 'all', 40) || 'all',
    event: sanitizeToken(params.get('event') ?? '', 120),
    market: sanitizeToken(params.get('market') ?? '', 120),
    marketType: sanitizeMarketType(params.get('mtype') ?? 'all'),
    side: SIDES.has(sideRaw as BetSide | 'all') ? (sideRaw as BetSide | 'all') : 'all',
    status: sanitizeStatus(params.get('status') ?? 'all'),
    currency: sanitizeCurrency(params.get('ccy') ?? 'all'),
    from,
    to,
    stakeMin,
    stakeMax,
    page,
    pageSize,
  }
}

export function serializeTickerFilters(filters: TickerFilters): URLSearchParams {
  const params = new URLSearchParams()
  const d = DEFAULT_TICKER_FILTERS
  if (filters.search) params.set('q', filters.search)
  for (const id of filters.members) params.append('member', id)
  if (filters.sport && filters.sport !== d.sport) params.set('sport', filters.sport)
  if (filters.event) params.set('event', filters.event)
  if (filters.market) params.set('market', filters.market)
  if (filters.marketType !== d.marketType) params.set('mtype', filters.marketType)
  if (filters.side !== d.side) params.set('side', filters.side)
  if (filters.status !== d.status) params.set('status', filters.status)
  if (filters.currency !== d.currency) params.set('ccy', filters.currency)
  if (filters.from) params.set('from', filters.from)
  if (filters.to) params.set('to', filters.to)
  if (filters.stakeMin != null) params.set('smin', String(filters.stakeMin))
  if (filters.stakeMax != null) params.set('smax', String(filters.stakeMax))
  if (filters.page !== 1) params.set('page', String(filters.page))
  if (filters.pageSize !== d.pageSize) params.set('size', String(filters.pageSize))
  return params
}

export function tickerSearchString(filters: TickerFilters): string {
  const q = serializeTickerFilters(filters).toString()
  return q ? `?${q}` : ''
}

export function filtersAreActive(filters: TickerFilters): boolean {
  const d = DEFAULT_TICKER_FILTERS
  return (
    filters.members.length > 0 ||
    filters.search !== d.search ||
    filters.sport !== d.sport ||
    Boolean(filters.event) ||
    Boolean(filters.market) ||
    filters.marketType !== d.marketType ||
    filters.side !== d.side ||
    filters.status !== d.status ||
    filters.currency !== d.currency ||
    Boolean(filters.from) ||
    Boolean(filters.to) ||
    filters.stakeMin != null ||
    filters.stakeMax != null
  )
}

/** Filters that live behind More — for the badge count. Members sit on the primary bar. */
export function moreFilterCount(filters: TickerFilters, previous: boolean): number {
  let n = 0
  if (filters.event) n += 1
  if (filters.marketType !== 'all') n += 1
  if (filters.market) n += 1
  if (filters.stakeMin != null || filters.stakeMax != null) n += 1
  if (!previous && (filters.from || filters.to)) n += 1
  return n
}

/**
 * Lab Sub Game / Event cascade: changing sport clears Event+Market;
 * changing event clears Market only.
 */
export function cascadeSportPatch(sport: string): Partial<TickerFilters> {
  return { sport, event: '', market: '' }
}

export function cascadeEventPatch(event: string): Partial<TickerFilters> {
  return { event, market: '' }
}

export function patchTickerFilters(
  current: TickerFilters,
  patch: Partial<TickerFilters>,
): TickerFilters {
  const next = { ...current, ...patch }
  next.search = sanitizeSearch(next.search)
  next.members = next.members
    .map(sanitizeMember)
    .filter((id): id is string => Boolean(id))
    .slice(0, MEMBER_MAX)
  next.currency = sanitizeCurrency(next.currency)
  next.status = sanitizeStatus(next.status)
  next.marketType = sanitizeMarketType(next.marketType)
  const range = clampDateRange(next.from, next.to)
  next.from = range.from
  next.to = range.to
  if (next.stakeMin != null && next.stakeMax != null && next.stakeMin > next.stakeMax) {
    const swap = next.stakeMin
    next.stakeMin = next.stakeMax
    next.stakeMax = swap
  }
  const pageTouched = patch.page !== undefined
  const sizeTouched = patch.pageSize !== undefined
  const other = Object.keys(patch).some((key) => key !== 'page' && key !== 'pageSize')
  if (other && !pageTouched) next.page = 1
  if (sizeTouched && !pageTouched) next.page = 1
  if (!PAGE_SIZES.has(next.pageSize)) next.pageSize = DEFAULT_PAGE_SIZE
  if (!Number.isInteger(next.page) || next.page < 1) next.page = 1
  return next
}

/** Previous is a history query: the API refuses it without both dates. */
export function previousRangeMissing(filters: TickerFilters): boolean {
  return !filters.from || !filters.to
}

function localIsoDay(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function parseLocalDay(iso: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return null
  const dt = new Date(y, m - 1, d)
  return Number.isNaN(dt.getTime()) ? null : dt
}

/** A sensible opening window for Previous — the last week, ending today. */
export function defaultPreviousRange(today = new Date()): { from: string; to: string } {
  const to = localIsoDay(today)
  const fromDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6)
  return { from: localIsoDay(fromDate), to }
}

/**
 * Lab `/betfair-bet-ticker` → BigQuery when the span exceeds 7 days or `to` is not today.
 * Used by Bet Ticker Previous (merged Previous New) for `ticker` vs `tickerBQ`.
 */
export function shouldUseTickerBq(
  from: string,
  to: string,
  now: Date = new Date(),
): boolean {
  const fromDate = parseLocalDay(from)
  const toDate = parseLocalDay(to)
  if (!fromDate || !toDate) return false
  const diffDays = Math.floor(
    Math.abs(toDate.getTime() - fromDate.getTime()) / 86_400_000,
  )
  if (diffDays > TICKER_BQ_SPAN_DAYS) return true
  return localIsoDay(now) !== to
}
